import React, { useState, useEffect, useRef } from 'react';
import { getActiveResidences } from '../services/residenceService';
import './ResidenceSelector.css';

/**
 * ResidenceSelector Component
 * A reusable dropdown selector for choosing residences
 * Ready for database integration
 * 
 * @param {string} selectedResidence - Currently selected residence
 * @param {function} onResidenceSelect - Callback when residence is selected
 * @param {string} label - Label text to display (optional)
 * @param {boolean} showIcon - Whether to show location icon (default: true)
 */
export default function ResidenceSelector({ 
  selectedResidence, 
  onResidenceSelect, 
  label = 'Currently showing food available in',
  showIcon = true 
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [residences, setResidences] = useState([]);
  const dropdownRef = useRef(null);

  // Fetch residences from database
  useEffect(() => {
    const fetchResidences = async () => {
      try {
        // Fetch from database service (uses mock data for now, ready for Appwrite)
        const fetchedResidences = await getActiveResidences();
        setResidences(fetchedResidences);
      } catch (error) {
        console.error('Error fetching residences:', error);
        // Set empty array on error
        setResidences([]);
      }
    };

    fetchResidences();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleResidenceClick = (residence) => {
    onResidenceSelect(residence.name);
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="residence-selector-container" ref={dropdownRef}>
      {label && <span className="residence-label">{label}</span>}
      <div className="residence-selector" onClick={toggleDropdown}>
        {showIcon && <span className="residence-icon">📍</span>}
        <span className="residence-name">
          {selectedResidence || 'Select your residence'}
        </span>
        <span className={`residence-dropdown-icon ${isOpen ? 'open' : ''}`}>▼</span>
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="residence-dropdown-menu">
          {residences.length === 0 ? (
            <div className="residence-dropdown-item disabled">
              No other residences available
            </div>
          ) : (
            residences.map((residence) => (
              <div
                key={residence.id}
                className={`residence-dropdown-item ${
                  selectedResidence === residence.name ? 'selected' : ''
                }`}
                onClick={() => handleResidenceClick(residence)}
              >
                {residence.name}
                {selectedResidence === residence.name && (
                  <span className="checkmark">✓</span>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
